import ComponentWithSocket from '../../../lib/component';
import { Component } from 'vue-property-decorator';

import * as Store from '../../../store';
import * as LabelPrint from '../../../utils/label-print';
import * as Stock from '../../../utils/stock';
import * as DirectPrinter from '../../../utils/direct-printer';

const SETTINGS_KEY = 'stock_label_settings';
const PAGE_STYLE_ID = 'eticheta-page-size';

interface LabelPreset {
    key: string;
    label: string;
    width: number;
    height: number;
}

/** Reglajele unei role pentru printarea directa, in mm. */
interface RollSettings {
    /** Deplasarea desenului spre dreapta; negativ inseamna spre stanga. */
    offsetX: number;
    /**
     * Cat de jos fata de marginea etichetei incepe imprimanta sa printeze.
     * Macheta lasa aceeasi margine si jos, ca desenul sa ramana centrat.
     */
    deadZone: number;
}

/*
 * Rolele de etichete folosite cu Datecs DPP-450 (latime utila ~104 mm, 203
 * dpi). Fiecare rola sta altfel sub cap, deci isi pastreaza propriile reglaje.
 */
const PRESETS: LabelPreset[] = [
    { key: '100x70', label: '100 × 70 mm', width: 100, height: 70 },
    { key: '70x40', label: '70 × 40 mm', width: 70, height: 40 },
];

const CUSTOM_PRESET = 'custom';

/*
 * Dupa avansul la eticheta urmatoare, DPP-450 lasa eticheta in pozitia de
 * rupere, cu capul la ~3 mm in interiorul ei. Nu are cum sa dea hartia
 * inapoi, deci acei mm nu se pot printa. Daca desenul trece in spatiul dintre
 * etichete, imprimanta se opreste mai tarziu (~6 mm) - de aceea pe etichete
 * se printeaza doar benzi care incap. Hartia continua (personalizat) n-are
 * aceasta problema: acolo ramane doar marginea de siguranta.
 */
const LABEL_DEAD_ZONE_MM = 3;
const SAFE_MARGIN_MM = 3;

function defaultRolls(): { [preset: string]: RollSettings } {
    const rolls: { [preset: string]: RollSettings } = {
        [CUSTOM_PRESET]: { offsetX: 0, deadZone: SAFE_MARGIN_MM },
    };
    PRESETS.forEach((preset) => {
        rolls[preset.key] = { offsetX: 0, deadZone: LABEL_DEAD_ZONE_MM };
    });
    return rolls;
}

const CONNECTIONS = DirectPrinter.supportedConnections();

const CONNECTION_LABELS: { [connection: string]: string } = {
    usb: 'USB (cablu)',
    bluetooth: 'Bluetooth',
};

/** Pe telefon imprimanta se leaga pe Bluetooth; pe calculator, pe cablu. */
function defaultConnection(): DirectPrinter.Connection {
    const preferred: DirectPrinter.Connection = /Android/i.test(
        navigator.userAgent,
    )
        ? 'bluetooth'
        : 'usb';

    return CONNECTIONS.indexOf(preferred) >= 0 ? preferred : CONNECTIONS[0];
}

/**
 * Pagina de printare a etichetelor cu cod QR. Se deschide fie pentru toate
 * loturile unui contract (`?contract=<id>`), fie pentru loturi alese
 * individual (`?lots=<id>,<id>`).
 */
@Component
export default class StockLabels extends ComponentWithSocket {
    public data: any = {
        lots: [],
        loaded: false,
        preset: '100x70',
        width: 100,
        height: 70,
        thermal: true,
        labelRoll: true,
        // Toate cheile exista de la inceput, ca Vue sa le urmareasca.
        rolls: defaultRolls(),
        connection: defaultConnection(),
        directPrinting: false,
        directMessage: '',
        directFailed: false,
    };

    formatContractNumber = Stock.formatContractNumber;
    labelUrl = Stock.stockLabelUrl;

    presets = PRESETS;

    connectionOptions = CONNECTIONS.map((value) => ({
        value,
        label: CONNECTION_LABELS[value],
    }));

    constructor() {
        super();

        this.wsListener.onGet('stock.lots', (success: boolean, data: any) => {
            Store.commitLoaderVisibility(this.$store, false);
            this.data.loaded = true;

            if (!success) {
                return alert(data);
            }

            this.data.lots = data.lots;
        });
    }

    mounted() {
        this.restoreSettings();
        this.applyPageSize();
        this.load();
    }

    beforeRouteLeave(to: any, from: any, next: any) {
        this.removePageSize();
        this._beforeLeave(to, from);
        next();
    }

    /**
     * Dimensiunea paginii de printare. Nu poate sta in fisierul .scss: regula
     * `@page` nu rezolva variabile CSS, deci se scrie aici cu valori literale.
     */
    applyPageSize() {
        let style = document.getElementById(
            PAGE_STYLE_ID,
        ) as HTMLStyleElement | null;

        if (!style) {
            style = document.createElement('style');
            style.id = PAGE_STYLE_ID;
            document.head.appendChild(style);
        }

        style.textContent =
            '@media print { @page { size: ' +
            `${this.data.width}mm ${this.data.height}mm` +
            '; margin: 0; } }';
    }

    removePageSize() {
        const style = document.getElementById(PAGE_STYLE_ID);
        if (style && style.parentNode) {
            style.parentNode.removeChild(style);
        }
    }

    load() {
        const query = this.$route.query;
        const filters: any = { perPage: 0 };

        if (query.contract) {
            filters.contract = query.contract;
        } else if (query.lots) {
            filters.lotIds = String(query.lots).split(',');
        } else {
            this.data.loaded = true;
            return;
        }

        Store.commitLoaderVisibility(this.$store, true);
        this.wsListener.sendGet('stock.lots', filters);
    }

    /** Autorul, cu revenire pe atributul "Autor" de pe lot. */
    artistName(lot: any): string {
        if (lot.author && lot.author.name) return lot.author.name;

        const attribute = (lot.attributes || []).find(
            (item: any) => item.name === 'Autor' && item.value,
        );

        return attribute ? attribute.value : '';
    }

    /**
     * Inversul lungimii numarului de contract, din care CSS-ul ii calculeaza
     * marimea. E dat ca invers ca formula din CSS sa nu aiba nevoie de
     * paranteze (vezi `.eticheta-contract`).
     */
    contractStyle(lot: any) {
        const length = Stock.formatContractNumber(lot.contract).length;
        return { '--inv-chars': (1 / (length || 1)).toFixed(4) };
    }

    /** Reglajele rolei alese acum. */
    get roll(): RollSettings {
        return (
            this.data.rolls[this.data.preset] || this.data.rolls[CUSTOM_PRESET]
        );
    }

    get styleVars() {
        // Previzualizarea arata aceleasi margini ca eticheta printata.
        const marginY = Math.max(SAFE_MARGIN_MM, Number(this.roll.deadZone) || 0);

        return {
            '--label-w': `${this.data.width}mm`,
            '--label-h': `${this.data.height}mm`,
            '--label-margin-y': `${marginY}mm`,
        };
    }

    onPresetChange() {
        const preset = PRESETS.find(item => item.key === this.data.preset);

        if (preset) {
            this.data.width = preset.width;
            this.data.height = preset.height;
        }

        this.applyPageSize();
        this.persistSettings();
    }

    onSizeChange() {
        // Dimensiunile scrise de mana nu mai corespund niciunui preset.
        this.data.preset = CUSTOM_PRESET;
        this.applyPageSize();
        this.persistSettings();
    }

    persistSettings() {
        try {
            localStorage.setItem(
                SETTINGS_KEY,
                JSON.stringify({
                    preset: this.data.preset,
                    width: this.data.width,
                    height: this.data.height,
                    thermal: this.data.thermal,
                    labelRoll: this.data.labelRoll,
                    rolls: this.data.rolls,
                    connection: this.data.connection,
                }),
            );
        } catch (e) {
            // Fara localStorage se pierd doar setarile, nu si etichetele.
        }
    }

    restoreSettings() {
        try {
            const raw = localStorage.getItem(SETTINGS_KEY);
            if (!raw) return;

            const stored = JSON.parse(raw);
            const knownPreset =
                stored.preset === CUSTOM_PRESET ||
                PRESETS.some((item) => item.key === stored.preset);

            // Un preset scos intre timp ramane ca dimensiune personalizata.
            this.data.preset = knownPreset ? stored.preset : CUSTOM_PRESET;
            this.data.width = stored.width || this.data.width;
            this.data.height = stored.height || this.data.height;
            this.data.thermal =
                stored.thermal === undefined ? true : !!stored.thermal;
            this.data.labelRoll =
                stored.labelRoll === undefined ? true : !!stored.labelRoll;
            Object.keys(this.data.rolls).forEach((key) => {
                const saved = (stored.rolls || {})[key];
                if (!saved) return;

                const roll: RollSettings = this.data.rolls[key];
                roll.offsetX = Number(saved.offsetX) || 0;
                if (saved.deadZone !== undefined) {
                    roll.deadZone = Number(saved.deadZone) || 0;
                }
            });

            if (CONNECTIONS.indexOf(stored.connection) >= 0) {
                this.data.connection = stored.connection;
            }
        } catch (e) {
            // Setari corupte: ramanem pe valorile implicite.
        }
    }

    doPrint() {
        window.print();
    }

    /**
     * Trimite etichetele direct la Datecs DPP-450, pe USB sau Bluetooth, fara
     * dialogul de print.
     */
    async doDirectPrint() {
        if (this.data.directPrinting || !this.data.lots.length) return;

        const connection: DirectPrinter.Connection = this.data.connection;
        this.data.directPrinting = true;
        this.data.directMessage = '';

        try {
            // Lista din Chrome cere un click recent, deci imprimanta se alege
            // inainte de randarea etichetelor.
            const send = await DirectPrinter.connectPrinter(connection);

            // Imprimanta e monocroma: varianta cu auriu nu are ce cauta aici.
            this.data.thermal = true;
            this.persistSettings();
            await this.$nextTick();

            const labels = Array.from(
                this.$el.querySelectorAll('.eticheta'),
            ) as HTMLElement[];
            const job = await LabelPrint.buildLabelJob(labels, {
                labelRoll: this.data.labelRoll,
                offsetXMm: Number(this.roll.offsetX) || 0,
                deadZoneMm: Number(this.roll.deadZone) || 0,
            });
            await send(job);

            this.data.directFailed = false;
            this.data.directMessage = `${labels.length} etichete trimise la imprimanta.`;
        } catch (e) {
            this.data.directFailed = true;
            this.data.directMessage = DirectPrinter.directPrintErrorMessage(
                e,
                connection,
            );
        } finally {
            this.data.directPrinting = false;
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/stock/labels/index.ts